import { Link } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Overview from "@/components/admin/Overview";
import ProductsTab from "@/components/admin/ProductsTab";
import CategoriesTab from "@/components/admin/CategoriesTab";
import OrdersTab from "@/components/admin/OrdersTab";
import CouponsTab from "@/components/admin/CouponsTab";
import WholesaleTab from "@/components/admin/WholesaleTab";
import ContentTab from "@/components/admin/ContentTab";

const AdminLogin = () => {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-5" data-testid="admin-login-screen">
      <div className="w-full max-w-sm rounded-2xl border border-linen bg-white p-8 text-center shadow-[0_20px_50px_-20px_rgba(46,36,31,0.25)]">
        <div className="flex justify-center"><Logo /></div>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.28em] text-terra">Admin portal</p>
        <h1 className="mt-2 font-serif text-2xl text-espresso">Owner sign-in</h1>
        <p className="mt-2 text-sm text-clay/60">Manage products, stock, prices, coupons, orders and wholesale enquiries.</p>
        <button
          data-testid="admin-login-submit-button"
          onClick={login}
          className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-espresso py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-terra active:scale-[0.98]"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M21.35 11.1H12v3.9h5.35c-.5 2.4-2.55 3.9-5.35 3.9a5.9 5.9 0 1 1 0-11.8c1.5 0 2.85.55 3.9 1.45l2.8-2.8A9.9 9.9 0 1 0 12 21.9c5.7 0 9.5-4 9.5-9.65 0-.35-.05-.75-.15-1.15z"/></svg>
          Continue with Google
        </button>
        <Link to="/" className="mt-5 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-clay/50 transition-colors hover:text-terra">
          ← Back to store
        </Link>
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="h-6 w-6 animate-spin text-terra" />
      </div>
    );
  }
  if (!user) return <AdminLogin />;
  if (user.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-5" data-testid="admin-access-denied">
        <div className="w-full max-w-sm rounded-2xl border border-linen bg-white p-8 text-center">
          <h1 className="font-serif text-2xl text-espresso">Admins only</h1>
          <p className="mt-3 text-sm text-clay/60">
            <span className="font-mono">{user.email}</span> doesn't have admin access. Sign in with the owner account.
          </p>
          <button onClick={logout} className="mt-6 w-full rounded-full bg-espresso py-3 text-sm font-semibold text-cream hover:bg-terra">
            Switch account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-linen bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-4">
            <Link to="/"><Logo compact /></Link>
            <span className="rounded-full bg-terra-light px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-terra-dark">Admin · Live</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-clay/60 sm:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 rounded-full border border-linen px-3.5 py-2 text-xs font-semibold text-clay transition-colors hover:border-terra hover:text-terra">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <h1 className="font-serif text-3xl tracking-tight text-espresso">Dashboard</h1>
        <p className="mt-1 text-sm text-clay/60">Inventory, orders, coupons, wholesale and sales — all in one place.</p>

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-full border border-linen bg-white p-1.5">
            <TabsTrigger data-testid="admin-tab-overview" value="overview" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Overview</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-products" value="products" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Products</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-categories" value="categories" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Categories</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-orders" value="orders" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Orders</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-coupons" value="coupons" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Coupons</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-wholesale" value="wholesale" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Wholesale</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-content" value="content" className="rounded-full px-5 data-[state=active]:bg-espresso data-[state=active]:text-cream">Site Content</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-8"><Overview /></TabsContent>
          <TabsContent value="products" className="mt-8"><ProductsTab /></TabsContent>
          <TabsContent value="categories" className="mt-8"><CategoriesTab /></TabsContent>
          <TabsContent value="orders" className="mt-8"><OrdersTab /></TabsContent>
          <TabsContent value="coupons" className="mt-8"><CouponsTab /></TabsContent>
          <TabsContent value="wholesale" className="mt-8"><WholesaleTab /></TabsContent>
          <TabsContent value="content" className="mt-8"><ContentTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
